// Smart Sash Admin Panel - Stream Controls Component

import { Button } from '@/components/common';
import { ViewerStatus } from '@/hooks/useStreamViewer';
import React from 'react';

interface StreamControlsProps {
  isMuted: boolean;
  volume: number;
  isFullscreen: boolean;
  viewerStatus: ViewerStatus;
  onToggleMute: () => void;
  onVolumeChange: (volume: number) => void;
  onToggleFullscreen: () => void;
  onLeave: () => void;
}

const StatusBadge: React.FC<{ status: ViewerStatus }> = ({ status }) => {
  switch (status) {
    case 'loading':
    case 'connecting':
      return (
        <span className="badge bg-secondary d-flex align-items-center gap-1">
          <i className="fas fa-circle-notch fa-spin" style={{ fontSize: '0.65rem' }} />
          Connecting...
        </span>
      );
    case 'connected':
      return (
        <span className="badge bg-success d-flex align-items-center gap-1">
          <i className="fas fa-circle" style={{ fontSize: '0.5rem' }} />
          Connected
        </span>
      );
    case 'reconnecting':
      return (
        <span className="badge bg-warning text-dark d-flex align-items-center gap-1">
          <i className="fas fa-sync fa-spin" style={{ fontSize: '0.65rem' }} />
          Reconnecting...
        </span>
      );
    case 'disconnected':
      return (
        <span className="badge bg-secondary d-flex align-items-center gap-1">
          <i className="fas fa-unlink" style={{ fontSize: '0.65rem' }} />
          Disconnected
        </span>
      );
    case 'error':
      return (
        <span className="badge bg-danger d-flex align-items-center gap-1">
          <i className="fas fa-exclamation-circle" style={{ fontSize: '0.65rem' }} />
          Connection error
        </span>
      );
    default:
      return null;
  }
};

export const StreamControls: React.FC<StreamControlsProps> = ({
  isMuted,
  volume,
  isFullscreen,
  viewerStatus,
  onToggleMute,
  onVolumeChange,
  onToggleFullscreen,
  onLeave,
}) => {
  return (
    <div className="d-flex align-items-center justify-content-between px-3 py-2 bg-dark rounded">
      {/* Volume controls */}
      <div className="d-flex align-items-center gap-2">
        <button
          type="button"
          className="btn btn-sm btn-outline-light border-0"
          onClick={onToggleMute}
          title={isMuted ? 'Unmute' : 'Mute'}
        >
          <i className={`fas ${isMuted || volume === 0 ? 'fa-volume-mute' : 'fa-volume-up'}`} />
        </button>
        <input
          type="range"
          className="form-range"
          min={0}
          max={1}
          step={0.05}
          value={isMuted ? 0 : volume}
          onChange={(e) => onVolumeChange(parseFloat(e.target.value))}
          style={{ width: '80px' }}
        />
      </div>

      {/* Status */}
      <StatusBadge status={viewerStatus} />

      {/* Fullscreen & Leave */}
      <div className="d-flex align-items-center gap-2">
        <button
          type="button"
          className="btn btn-sm btn-outline-light border-0"
          onClick={onToggleFullscreen}
          title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
        >
          <i className={`fas ${isFullscreen ? 'fa-compress' : 'fa-expand'}`} />
        </button>
        <Button variant="danger" size="sm" onClick={onLeave} icon="fas fa-sign-out-alt">
          Leave
        </Button>
      </div>
    </div>
  );
};
